import { useState, useEffect, useRef, useCallback } from 'react';
import styles from './WhatsappInbox.module.css';
import SideBarGestaoAdmin from '../components/SideBarGestaoAdmin';
import MobileMenu from '../../../shared/components/MobileMenu';
import HeaderAdmin from '../components/HeaderAdmin';
import { MdSend, MdCheckCircle, MdSupportAgent, MdPerson, MdSmartToy, MdReceiptLong, MdClose, MdAdd, MdRemove, MdChatBubbleOutline, MdOutlineWhatsapp, MdUploadFile, MdArrowBack } from 'react-icons/md';
import { sseService } from '../../../shared/services/sse';

const API_URL = process.env.REACT_APP_API_URL;

function cabecalhosAutenticados() {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };
}

// Rótulo amigável pra etapa_atual da sessão — o resto dos nomes (aguardando_nome,
// montando_pedido...) é jargão interno da máquina de estados do bot.
function rotuloEtapa(etapa) {
  const rotulos = {
    inicio: 'Início da conversa',
    aguardando_nome: 'Bot: coletando nome',
    aguardando_confirmacao_cadastro: 'Bot: confirmando cadastro',
    menu_principal: 'Bot: no cardápio',
    aguardando_escolha_item_ambiguo: 'Bot: aguardando escolha do item',
    aguardando_quantidade: 'Bot: perguntando quantidade',
    montando_pedido: 'Bot: montando pedido',
    aguardando_tipo_entrega: 'Bot: perguntando entrega',
    aguardando_forma_pagamento: 'Bot: perguntando pagamento',
    atendimento_humano: 'Aguardando atendente'
  };
  return rotulos[etapa] || etapa;
}

// Alguns itens do cardápio têm um número solto na frente do nome (ex: "70
// Verão - Pequena") — resquício de um ID que foi colado junto no cadastro.
// Não mexe no dado em si, só esconde esse prefixo na exibição.
function formatarNomeItem(nome) {
  return nome.replace(/^\d+\s+/, '');
}

// Avatar circular coerente por contato: iniciais + uma cor tirada de uma
// paleta fixa (hash simples do telefone), pra cada conversa ter uma "cara"
// diferente sem precisar de foto de verdade.
const CORES_AVATAR = ['#d33333', '#2f6fed', '#d99a4e', '#4CAF50', '#8e5fd6', '#0891b2'];

function corAvatar(chave) {
  let hash = 0;
  for (let i = 0; i < chave.length; i++) hash = chave.charCodeAt(i) + ((hash << 5) - hash);
  return CORES_AVATAR[Math.abs(hash) % CORES_AVATAR.length];
}

function iniciaisContato(nome, phone) {
  const base = (nome || phone || '?').trim();
  return base.charAt(0).toUpperCase();
}

function formatarHorario(dataIso) {
  if (!dataIso) return '';
  const data = new Date(dataIso);
  return data.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function WhatsappInbox() {
  const [conversas, setConversas] = useState([]);
  const [carregandoConversas, setCarregandoConversas] = useState(true);
  const [phoneSelecionado, setPhoneSelecionado] = useState(null);
  const [mensagens, setMensagens] = useState([]);
  const [carregandoMensagens, setCarregandoMensagens] = useState(false);
  const [textoResposta, setTextoResposta] = useState('');
  const [enviando, setEnviando] = useState(false);
  const fimDaListaRef = useRef(null);

  // Pedido manual (atendente monta o pedido na mão, sem depender do cliente
  // voltar pro bot) — ver backend/controllers/whatsappInbox.controller.js:criarPedidoManual.
  const [mostrarPedidoManual, setMostrarPedidoManual] = useState(false);
  const [catalogo, setCatalogo] = useState([]);
  const [carregandoCatalogo, setCarregandoCatalogo] = useState(false);
  const [filtroCatalogo, setFiltroCatalogo] = useState('');
  const [carrinhoManual, setCarrinhoManual] = useState([]); // [{ id_produto, nome, preco, quantidade }]
  const [tipoEntregaManual, setTipoEntregaManual] = useState('Agora');
  const [formaPagamentoManual, setFormaPagamentoManual] = useState('Pix');
  const [criandoPedido, setCriandoPedido] = useState(false);

  // Importação de contatos (planilha nome+telefone) — ver
  // backend/controllers/whatsappInbox.controller.js:importarContatos.
  const [importandoContatos, setImportandoContatos] = useState(false);
  const inputArquivoContatosRef = useRef(null);

  // Aba "Contatos": todo whatsapp_customer do tenant, mesmo sem conversa
  // ainda — é onde dá pra conferir se a importação funcionou.
  const [abaAtiva, setAbaAtiva] = useState('conversas'); // 'conversas' | 'contatos'
  const [contatos, setContatos] = useState([]);
  const [carregandoContatos, setCarregandoContatos] = useState(false);

  const estabelecimentoId = localStorage.getItem('estabelecimento_id');

  const buscarConversas = useCallback(async () => {
    if (!estabelecimentoId) return;
    try {
      const res = await fetch(`${API_URL}/whatsapp/conversas?id_estabelecimento=${estabelecimentoId}`, {
        headers: cabecalhosAutenticados()
      });
      if (!res.ok) throw new Error('Falha ao buscar conversas');
      const data = await res.json();
      setConversas(data);
    } catch (error) {
      console.log('Erro ao buscar conversas do WhatsApp:', error.message);
    } finally {
      setCarregandoConversas(false);
    }
  }, [estabelecimentoId]);

  const buscarContatos = useCallback(async () => {
    if (!estabelecimentoId) return;
    setCarregandoContatos(true);
    try {
      const res = await fetch(`${API_URL}/whatsapp/contatos?id_estabelecimento=${estabelecimentoId}`, {
        headers: cabecalhosAutenticados()
      });
      if (!res.ok) throw new Error('Falha ao buscar contatos');
      const data = await res.json();
      setContatos(data);
    } catch (error) {
      console.log('Erro ao buscar contatos do WhatsApp:', error.message);
    } finally {
      setCarregandoContatos(false);
    }
  }, [estabelecimentoId]);

  // Carrega a lista de contatos só quando a aba é aberta a primeira vez.
  useEffect(() => {
    if (abaAtiva === 'contatos') buscarContatos();
  }, [abaAtiva, buscarContatos]);

  // Clicar num contato que já tem conversa pula pra aba de conversas nele;
  // um contato ainda sem conversa não tem pra onde ir (não existe sessão/
  // histórico dele ainda).
  const selecionarContato = (contato) => {
    if (!contato.tem_conversa) {
      alert(`${contato.nome} ainda não mandou nenhuma mensagem. Quando mandar, a conversa aparece na aba "Conversas" automaticamente.`);
      return;
    }
    setAbaAtiva('conversas');
    setPhoneSelecionado(contato.phone);
  };

  const buscarMensagens = useCallback(async (phone) => {
    if (!estabelecimentoId || !phone) return;
    setCarregandoMensagens(true);
    try {
      const res = await fetch(
        `${API_URL}/whatsapp/conversas/${phone}/mensagens?id_estabelecimento=${estabelecimentoId}`,
        { headers: cabecalhosAutenticados() }
      );
      if (!res.ok) throw new Error('Falha ao buscar mensagens');
      const data = await res.json();
      setMensagens(data);
    } catch (error) {
      console.log('Erro ao buscar mensagens:', error.message);
    } finally {
      setCarregandoMensagens(false);
    }
  }, [estabelecimentoId]);

  // Lista de conversas: busca inicial + atualização em tempo real via SSE
  // (mesmo mecanismo que já avisa a cozinha de pedido novo).
  useEffect(() => {
    buscarConversas();
    if (estabelecimentoId) sseService.connect(estabelecimentoId);

    const handleNovaMensagem = (dados) => {
      buscarConversas();
      // Se a conversa aberta na tela recebeu mensagem nova, recarrega o histórico também.
      setPhoneSelecionado(atual => {
        if (atual && dados?.mensagem?.phone === atual) buscarMensagens(atual);
        return atual;
      });
    };

    const handleAtendimentoSolicitado = (dados) => {
      buscarConversas();
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('🙋 Cliente pediu atendimento no WhatsApp', {
          body: dados?.nome ? `${dados.nome} (${dados.phone})` : dados?.phone,
          tag: `whatsapp-atendimento-${dados?.phone}`
        });
      }
    };

    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    sseService.on('whatsapp_mensagem', handleNovaMensagem);
    sseService.on('whatsapp_atendimento_solicitado', handleAtendimentoSolicitado);

    // Fallback por polling — cobre o caso do SSE cair sem reconectar a tempo.
    const intervalo = setInterval(buscarConversas, 20000);

    return () => {
      sseService.off('whatsapp_mensagem', handleNovaMensagem);
      sseService.off('whatsapp_atendimento_solicitado', handleAtendimentoSolicitado);
      clearInterval(intervalo);
    };
  }, [estabelecimentoId]);

  useEffect(() => {
    if (phoneSelecionado) buscarMensagens(phoneSelecionado);
  }, [phoneSelecionado, buscarMensagens]);

  useEffect(() => {
    fimDaListaRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [mensagens]);

  const conversaAtual = conversas.find(c => c.phone === phoneSelecionado) || null;

  const enviarResposta = async () => {
    const mensagem = textoResposta.trim();
    if (!mensagem || !phoneSelecionado || enviando) return;

    setEnviando(true);
    try {
      const res = await fetch(`${API_URL}/whatsapp/conversas/${phoneSelecionado}/responder`, {
        method: 'POST',
        headers: cabecalhosAutenticados(),
        body: JSON.stringify({ id_estabelecimento: estabelecimentoId, mensagem })
      });
      if (!res.ok) {
        const erro = await res.json().catch(() => ({}));
        throw new Error(erro.error || 'Falha ao enviar mensagem');
      }
      setTextoResposta('');
      await buscarMensagens(phoneSelecionado);
      await buscarConversas();
    } catch (error) {
      alert('Erro ao enviar mensagem: ' + error.message);
    } finally {
      setEnviando(false);
    }
  };

  const encerrarAtendimento = async () => {
    if (!phoneSelecionado) return;
    if (!window.confirm('Encerrar o atendimento e devolver essa conversa pro bot automático?')) return;

    try {
      const res = await fetch(`${API_URL}/whatsapp/conversas/${phoneSelecionado}/encerrar-atendimento`, {
        method: 'POST',
        headers: cabecalhosAutenticados(),
        body: JSON.stringify({ id_estabelecimento: estabelecimentoId })
      });
      if (!res.ok) throw new Error('Falha ao encerrar atendimento');
      await buscarMensagens(phoneSelecionado);
      await buscarConversas();
    } catch (error) {
      alert('Erro ao encerrar atendimento: ' + error.message);
    }
  };

  const buscarCatalogo = useCallback(async () => {
    if (!estabelecimentoId) return;
    setCarregandoCatalogo(true);
    try {
      const res = await fetch(`${API_URL}/cadastroprodutos?id_estabelecimento=${estabelecimentoId}`);
      if (!res.ok) throw new Error('Falha ao buscar cardápio');
      const data = await res.json();
      setCatalogo((data || []).filter(item => item.disponivel === 'sim'));
    } catch (error) {
      console.log('Erro ao buscar catálogo:', error.message);
    } finally {
      setCarregandoCatalogo(false);
    }
  }, [estabelecimentoId]);

  const abrirPedidoManual = () => {
    setCarrinhoManual([]);
    setFiltroCatalogo('');
    setTipoEntregaManual('Agora');
    setFormaPagamentoManual('Pix');
    setMostrarPedidoManual(true);
    if (!catalogo.length) buscarCatalogo();
  };

  const adicionarItemAoCarrinho = (item) => {
    setCarrinhoManual(atual => {
      const existente = atual.find(i => i.id_produto === item.id_item);
      if (existente) {
        return atual.map(i => i.id_produto === item.id_item ? { ...i, quantidade: i.quantidade + 1 } : i);
      }
      return [...atual, { id_produto: item.id_item, nome: formatarNomeItem(item.nome_item), preco: Number(item.preco), quantidade: 1 }];
    });
  };

  const alterarQuantidadeManual = (id_produto, delta) => {
    setCarrinhoManual(atual => atual
      .map(i => i.id_produto === id_produto ? { ...i, quantidade: i.quantidade + delta } : i)
      .filter(i => i.quantidade > 0)
    );
  };

  const totalPedidoManual = carrinhoManual.reduce((soma, i) => soma + i.preco * i.quantidade, 0);

  const catalogoFiltrado = catalogo.filter(item =>
    item.nome_item.toLowerCase().includes(filtroCatalogo.trim().toLowerCase())
  );

  const confirmarPedidoManual = async () => {
    if (!phoneSelecionado || !carrinhoManual.length || criandoPedido) return;

    setCriandoPedido(true);
    try {
      const res = await fetch(`${API_URL}/whatsapp/conversas/${phoneSelecionado}/criar-pedido`, {
        method: 'POST',
        headers: cabecalhosAutenticados(),
        body: JSON.stringify({
          id_estabelecimento: estabelecimentoId,
          itens: carrinhoManual.map(i => ({ id_produto: i.id_produto, quantidade: i.quantidade })),
          tipoEntrega: tipoEntregaManual,
          formaDePagamento: formaPagamentoManual
        })
      });
      if (!res.ok) {
        const erro = await res.json().catch(() => ({}));
        throw new Error(erro.error || 'Falha ao criar pedido');
      }
      setMostrarPedidoManual(false);
      await buscarMensagens(phoneSelecionado);
      await buscarConversas();
    } catch (error) {
      alert('Erro ao criar pedido: ' + error.message);
    } finally {
      setCriandoPedido(false);
    }
  };

  const abrirSeletorDeContatos = () => {
    const confirmou = window.confirm(
      'Importar contatos do WhatsApp\n\n' +
      'A planilha (.xlsx, .xls ou .csv) precisa ter estas colunas:\n' +
      '  • nome\n' +
      '  • sobrenome\n' +
      '  • telefone\n\n' +
      'A coluna "telefone" é obrigatória (com DDD; o DDI +55 é adicionado automaticamente se faltar). "sobrenome" é opcional.\n\n' +
      'Clique OK pra escolher o arquivo.'
    );
    if (!confirmou) return;
    inputArquivoContatosRef.current?.click();
  };

  const importarContatos = async (evento) => {
    const arquivo = evento.target.files?.[0];
    evento.target.value = ''; // permite escolher o mesmo arquivo de novo depois
    if (!arquivo || importandoContatos) return;

    setImportandoContatos(true);
    try {
      const formData = new FormData();
      formData.append('id_estabelecimento', estabelecimentoId);
      formData.append('arquivo', arquivo);

      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/whatsapp/contatos/importar`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {}, // sem Content-Type: o navegador define o boundary do multipart sozinho
        body: formData
      });

      const dados = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(dados.error || 'Falha ao importar contatos');

      alert(`Importação concluída!\nNovos: ${dados.importados}\nAtualizados: ${dados.atualizados}\nIgnorados (sem telefone válido): ${dados.ignorados}`);
      await buscarConversas();
      await buscarContatos();
      setAbaAtiva('contatos');
    } catch (error) {
      alert('Erro ao importar contatos: ' + error.message);
    } finally {
      setImportandoContatos(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      enviarResposta();
    }
  };

  return (
    <div className={styles.wrapperDashboard}>
      <MobileMenu />
      <SideBarGestaoAdmin />
      <div className={styles.containerDashboard}>
        <HeaderAdmin />
        <div className={styles.contentDashboard}>
          <div className={`${styles.cabecalhoPagina} ${phoneSelecionado ? styles.ocultoMobile : ''}`}>
            <span className={styles.iconePagina}><MdOutlineWhatsapp /></span>
            <div className={styles.cabecalhoPaginaTextos}>
              <h2 className={styles.tituloPagina}>WhatsApp — Atendimento</h2>
              <p className={styles.subtituloPagina}>Converse com clientes e acompanhe o que o bot já resolveu sozinho.</p>
            </div>
            <button className={styles.btnImportarContatos} onClick={abrirSeletorDeContatos} disabled={importandoContatos}>
              <MdUploadFile /> <span>{importandoContatos ? 'Importando...' : 'Importar contatos'}</span>
            </button>
            <input
              ref={inputArquivoContatosRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              hidden
              onChange={importarContatos}
            />
          </div>

          <div className={`${styles.inboxLayout} ${phoneSelecionado ? styles.modoConversa : ''}`}>
            {/* Lista de conversas / contatos */}
            <div className={styles.colunaLista}>
              <div className={styles.abasLista}>
                <button
                  className={`${styles.abaLista} ${abaAtiva === 'conversas' ? styles.abaListaAtiva : ''}`}
                  onClick={() => setAbaAtiva('conversas')}
                >
                  Conversas
                </button>
                <button
                  className={`${styles.abaLista} ${abaAtiva === 'contatos' ? styles.abaListaAtiva : ''}`}
                  onClick={() => setAbaAtiva('contatos')}
                >
                  Contatos {contatos.length > 0 && `(${contatos.length})`}
                </button>
              </div>

              <div className={styles.listaConversas}>
                {abaAtiva === 'conversas' ? (
                  carregandoConversas ? (
                    <p className={styles.mensagemVazia}>Carregando conversas...</p>
                  ) : conversas.length === 0 ? (
                    <p className={styles.mensagemVazia}>Nenhuma conversa de WhatsApp ainda.</p>
                  ) : (
                    conversas.map(conversa => (
                      <button
                        key={conversa.phone}
                        className={`${styles.itemConversa} ${phoneSelecionado === conversa.phone ? styles.itemConversaAtivo : ''}`}
                        onClick={() => setPhoneSelecionado(conversa.phone)}
                      >
                        <span className={styles.avatar} style={{ backgroundColor: corAvatar(conversa.phone) }}>
                          {iniciaisContato(conversa.nome, conversa.phone)}
                        </span>
                        <div className={styles.itemConversaTexto}>
                          <div className={styles.itemConversaTopo}>
                            <span className={styles.itemConversaNome}>{conversa.nome || conversa.phone}</span>
                            {conversa.aguardando_atendente && (
                              <span className={styles.badgeAguardando}><MdSupportAgent /> atendente</span>
                            )}
                          </div>
                          <span className={styles.itemConversaEtapa}>{rotuloEtapa(conversa.etapa_atual)}</span>
                          {conversa.ultima_mensagem && (
                            <span className={styles.itemConversaPreview}>
                              {conversa.ultima_mensagem.autor === 'cliente' ? '' : '↳ '}
                              {conversa.ultima_mensagem.conteudo}
                            </span>
                          )}
                        </div>
                      </button>
                    ))
                  )
                ) : (
                  carregandoContatos ? (
                    <p className={styles.mensagemVazia}>Carregando contatos...</p>
                  ) : contatos.length === 0 ? (
                    <p className={styles.mensagemVazia}>Nenhum contato importado ainda.</p>
                  ) : (
                    contatos.map(contato => (
                      <button
                        key={contato.id}
                        className={styles.itemConversa}
                        onClick={() => selecionarContato(contato)}
                      >
                        <span className={styles.avatar} style={{ backgroundColor: corAvatar(contato.phone) }}>
                          {iniciaisContato(contato.nome, contato.phone)}
                        </span>
                        <div className={styles.itemConversaTexto}>
                          <div className={styles.itemConversaTopo}>
                            <span className={styles.itemConversaNome}>{contato.nome}</span>
                            {!contato.tem_conversa && (
                              <span className={styles.badgeSemConversa}>sem conversa</span>
                            )}
                          </div>
                          <span className={styles.itemConversaPreview}>{contato.phone}</span>
                        </div>
                      </button>
                    ))
                  )
                )}
              </div>
            </div>

            {/* Painel da conversa selecionada */}
            <div className={styles.painelConversa}>
              {!phoneSelecionado ? (
                <div className={styles.estadoVazio}>
                  <MdChatBubbleOutline className={styles.iconeEstadoVazio} />
                  <p>Selecione uma conversa pra ver o histórico e responder.</p>
                </div>
              ) : (
                <>
                  <div className={styles.painelHeader}>
                    <div className={styles.painelHeaderInfo}>
                      <button className={styles.btnVoltar} onClick={() => setPhoneSelecionado(null)} aria-label="Voltar para a lista">
                        <MdArrowBack />
                      </button>
                      <span className={styles.avatar} style={{ backgroundColor: corAvatar(phoneSelecionado) }}>
                        {iniciaisContato(conversaAtual?.nome, phoneSelecionado)}
                      </span>
                      <div>
                        <strong className={styles.painelHeaderNome}>{conversaAtual?.nome || phoneSelecionado}</strong>
                        <span className={styles.painelHeaderSub}>{phoneSelecionado} · {rotuloEtapa(conversaAtual?.etapa_atual)}</span>
                      </div>
                    </div>
                  </div>

                  <div className={styles.listaMensagens}>
                    {carregandoMensagens ? (
                      <p className={styles.mensagemVazia}>Carregando mensagens...</p>
                    ) : (
                      mensagens.map(msg => (
                        <div
                          key={msg.id}
                          className={`${styles.bolha} ${msg.direcao === 'entrada' ? styles.bolhaEntrada : styles.bolhaSaida} ${msg.autor === 'atendente' ? styles.bolhaAtendente : ''}`}
                        >
                          <div className={styles.bolhaAutor}>
                            {msg.autor === 'cliente' && <MdPerson />}
                            {msg.autor === 'bot' && <MdSmartToy />}
                            {msg.autor === 'atendente' && <MdSupportAgent />}
                            <span>{msg.autor === 'cliente' ? (conversaAtual?.nome || 'Cliente') : msg.autor === 'bot' ? 'Bot' : 'Você'}</span>
                          </div>
                          <p className={styles.bolhaConteudo}>{msg.conteudo}</p>
                          <span className={styles.bolhaHorario}>{formatarHorario(msg.created_at)}</span>
                        </div>
                      ))
                    )}
                    <div ref={fimDaListaRef} />
                  </div>

                  <div className={styles.acoesRodape}>
                    <button className={styles.btnPedidoManual} onClick={abrirPedidoManual}>
                      <MdReceiptLong /> <span>Montar pedido</span>
                    </button>
                    {conversaAtual?.aguardando_atendente && (
                      <button className={styles.btnEncerrar} onClick={encerrarAtendimento}>
                        <MdCheckCircle /> <span>Encerrar atendimento</span>
                      </button>
                    )}
                  </div>

                  <div className={styles.caixaResposta}>
                    <textarea
                      className={styles.inputResposta}
                      placeholder="Digite sua resposta e aperte Enter para enviar..."
                      value={textoResposta}
                      onChange={(e) => setTextoResposta(e.target.value)}
                      onKeyDown={handleKeyDown}
                      rows={2}
                    />
                    <button className={styles.btnEnviar} onClick={enviarResposta} disabled={enviando || !textoResposta.trim()}>
                      <MdSend />
                    </button>
                  </div>
                  {!conversaAtual?.aguardando_atendente && (
                    <p className={styles.avisoBot}>
                      O bot ainda está respondendo automaticamente essa conversa. Mandar uma mensagem aqui pausa o bot e assume o atendimento.
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {mostrarPedidoManual && (
        <div className={styles.modalOverlay} onClick={() => setMostrarPedidoManual(false)}>
          <div className={styles.modalPedido} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <strong>Montar pedido — {conversaAtual?.nome || phoneSelecionado}</strong>
              <button className={styles.btnFecharModal} onClick={() => setMostrarPedidoManual(false)}>
                <MdClose />
              </button>
            </div>

            <div className={styles.modalCorpo}>
              <div className={styles.colunaCatalogo}>
                <input
                  className={styles.inputBusca}
                  placeholder="Buscar item do cardápio..."
                  value={filtroCatalogo}
                  onChange={(e) => setFiltroCatalogo(e.target.value)}
                />
                <div className={styles.listaCatalogo}>
                  {carregandoCatalogo ? (
                    <p className={styles.mensagemVazia}>Carregando cardápio...</p>
                  ) : catalogoFiltrado.length === 0 ? (
                    <p className={styles.mensagemVazia}>Nenhum item encontrado.</p>
                  ) : (
                    catalogoFiltrado.map(item => (
                      <button key={item.id_item} className={styles.itemCatalogo} onClick={() => adicionarItemAoCarrinho(item)}>
                        <span className={styles.itemCatalogoNome}>{formatarNomeItem(item.nome_item)}</span>
                        <span className={styles.itemCatalogoPreco}>R$ {Number(item.preco).toFixed(2)}</span>
                      </button>
                    ))
                  )}
                </div>
              </div>

              <div className={styles.colunaCarrinho}>
                <strong className={styles.tituloColuna}>Pedido</strong>
                {carrinhoManual.length === 0 ? (
                  <p className={styles.mensagemVazia}>Clique num item do cardápio pra adicionar.</p>
                ) : (
                  <div className={styles.listaCarrinhoManual}>
                    {carrinhoManual.map(item => (
                      <div key={item.id_produto} className={styles.linhaCarrinhoManual}>
                        <span className={styles.linhaCarrinhoNome}>{item.nome}</span>
                        <div className={styles.controleQuantidade}>
                          <button onClick={() => alterarQuantidadeManual(item.id_produto, -1)}><MdRemove /></button>
                          <span>{item.quantidade}</span>
                          <button onClick={() => alterarQuantidadeManual(item.id_produto, 1)}><MdAdd /></button>
                        </div>
                        <span className={styles.linhaCarrinhoTotal}>R$ {(item.preco * item.quantidade).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                )}

                <div className={styles.blocoOpcoes}>
                  <span className={styles.tituloOpcao}>Entrega</span>
                  <div className={styles.grupoBotoesOpcao}>
                    {['Agora', 'Viagem'].map(opcao => (
                      <button
                        key={opcao}
                        className={`${styles.botaoOpcao} ${tipoEntregaManual === opcao ? styles.botaoOpcaoAtivo : ''}`}
                        onClick={() => setTipoEntregaManual(opcao)}
                      >
                        {opcao === 'Agora' ? '🛵 Entrega' : '🏃 Retirada'}
                      </button>
                    ))}
                  </div>
                </div>

                <div className={styles.blocoOpcoes}>
                  <span className={styles.tituloOpcao}>Pagamento</span>
                  <div className={styles.grupoBotoesOpcao}>
                    {['Pix', 'Cartão', 'Dinheiro'].map(opcao => (
                      <button
                        key={opcao}
                        className={`${styles.botaoOpcao} ${formaPagamentoManual === opcao ? styles.botaoOpcaoAtivo : ''}`}
                        onClick={() => setFormaPagamentoManual(opcao)}
                      >
                        {opcao}
                      </button>
                    ))}
                  </div>
                </div>

                <div className={styles.totalPedidoManual}>
                  <span>Total</span>
                  <strong>R$ {totalPedidoManual.toFixed(2)}</strong>
                </div>

                <button
                  className={styles.btnConfirmarPedido}
                  onClick={confirmarPedidoManual}
                  disabled={!carrinhoManual.length || criandoPedido}
                >
                  <span>{criandoPedido ? 'Criando pedido...' : 'Confirmar pedido'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WhatsappInbox;
